import React from 'react';
import { Link } from 'react-router-dom';
import { formatIST } from '@/lib/utils';

/**
 * Everything about the order that is a fact, in one dense band.
 *
 * This replaces two stacked cards — Customer Details and Order Info — that
 * between them held twelve values in the vertical space of about forty. The
 * same twelve now read across four columns at the top of the page, which is
 * what makes the rest of the screen fit without scrolling.
 */

const SOURCES = {
  phone_call: 'Phone Call',
  whatsapp: 'WhatsApp',
  email: 'Email',
  walk_in: 'Customer Walk-in',
};

const money = (value) => (value ? `₹${Number(value).toLocaleString()}` : '—');
const day = (iso) => (iso ? formatIST(iso, { dateOnly: true }) : '—');

function Fact({ label, children }) {
  return (
    <div className="flex items-stretch text-xs min-w-0">
      <span className="bg-slate-100 text-slate-600 px-2 py-1 font-medium whitespace-nowrap min-w-[5.5rem] border-r border-slate-200">
        {label}
      </span>
      <span className="flex-1 px-2 py-1 truncate text-slate-800">{children || '—'}</span>
    </div>
  );
}

export default function OrderFactsBand({ order, canSeeFinancials }) {
  const outstanding = (order.order_value || 0) - (order.total_paid || 0);

  return (
    <div className="grid gap-x-4 gap-y-px border border-slate-200 rounded p-1 md:grid-cols-2 xl:grid-cols-4"
      data-testid="order-facts">
      <Fact label="Customer">
        <Link to={`/customers/${order.customer_id}`} className="text-brand-blue hover:underline"
          data-testid="customer-link">
          {order.customer_name}
        </Link>
      </Fact>
      <Fact label="Firm">{order.firm_name}</Fact>
      <Fact label="Contact">{order.contact_number}</Fact>
      <Fact label="Source">{SOURCES[order.order_source] || order.order_source}</Fact>

      <Fact label="PO / Ref">{order.po_ref_number}</Fact>
      <Fact label="PO Date">{day(order.po_date)}</Fact>
      <Fact label="Order Date">{day(order.order_date)}</Fact>
      <Fact label="Delivery">{day(order.expected_delivery_date)}</Fact>

      <Fact label="Deliver To">{order.delivery_address}</Fact>
      <Fact label="Created By">{order.created_by_name}</Fact>
      <Fact label="Origin">{order.origin === 'city_office' ? 'City Office' : 'Main Office'}</Fact>
      <Fact label="Priority">
        <span className={
          order.priority === 'high' ? 'text-red-600 font-medium'
            : order.priority === 'low' ? 'text-emerald-600' : 'text-amber-600'
        }>
          {(order.priority || 'medium').toUpperCase()}
        </span>
      </Fact>

      {canSeeFinancials && (
        <>
          <Fact label="Order Value">{money(order.order_value)}</Fact>
          <Fact label="Paid">{money(order.total_paid)}</Fact>
          <Fact label="Outstanding">
            <span className={outstanding > 0 ? 'text-brand-orange font-medium' : 'text-emerald-600'}>
              {money(outstanding)}
            </span>
          </Fact>
          <Fact label="Disc / GST">
            {order.discount_pct || order.gst_pct
              ? `${order.discount_pct || 0}% / ${order.gst_pct || 0}%`
              : '—'}
          </Fact>
        </>
      )}
    </div>
  );
}
